import React, { useContext, useEffect, useState } from 'react';
import { AdminLayout } from "../../components/layouts";
import { DataContext } from "../../context/DataContext";
import { getNameInitials } from "../../utils";
import { fetchUserProfile, sendChatMessage, sendResponseFeedback } from "../../services/chatService";
const { v4: uuidv4 } = require('uuid');


const ChatComponent = () => {

    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [sessionId, setSessionId] = useState('');
    const [chatLog, setChatLog] = useState([]);
    const [userInfo, setUserInfo] = useState({})
    const [userInitials, setUserInitials] = useState('')
    const { userData } = useContext(DataContext)

    /******************************************************************
    * Form data for profile user
    *******************************************************************/
    const [formData, setFormData] = useState({
        user_email: '',
        gender: '',
        user_age: '',
        user_weight: '',
        user_height: '',
        user_complexion: '',
        user_religion: '',
        smoke: '',
        drink_alcohol: '',
        does_exercise: '',
        use_drugs: '',
        medicines: '',
        diseases: '',
        educational_level: '',
        personality: '',
        feelings: '',
        sexual_preference: '',
    });

    useEffect(() => {
        if (!userData || !userData.attributes) return;

        const { attributes } = userData;
        const initials = getNameInitials(attributes.name || "");
        setUserInfo(attributes);
        setUserInitials(initials);
        setFormData((prev) => ({ ...prev, user_email: attributes.email || "" }));

        const sessioID = uuidv4();
        setSessionId(sessioID);

        const loadProfile = async () => {
            try {
                const data = await fetchUserProfile(attributes.email);
                setFormData({
                    user_email: data.user_email.S,
                    gender: data.gender.S,
                    user_age: data.user_age.S.toString(),
                    user_weight: data.user_weight.S.toString(),
                    user_height: data.user_height.S.toString(),
                    user_complexion: data.user_complexion.S,
                    user_religion: data.user_religion.S,
                    smoke: data.smoke.S,
                    drink_alcohol: data.drink_alcohol.S,
                    does_exercise: data.does_exercise.S,
                    use_drugs: data.use_drugs.S,
                    medicines: data.medicines.S,
                    diseases: data.diseases.S,
                    educational_level: data.educational_level.S,
                    personality: data.personality.S,
                    feelings: data.feelings.S,
                    sexual_preference: data.sexual_preference.S,
                });
            } catch (error) {
                console.error('Error al obtener la información del usuario:', error);
            }
        };

        loadProfile();
    }, [userData]);


    /***
     * 
     * 
     */
    const handleInputChange = (e) => {
        setMessage(e.target.value);
    };

    const createChatEntry = (sender, text) => ({
        sender,
        text,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    });

    const handleSendMessage = async () => {
        if (!message.trim()) return;
        setLoading(true);
        try {
            const trimmedMessage = message.trim();
            const userEntry = createChatEntry('user', trimmedMessage);
            const currentLog = [...chatLog, userEntry];
            setChatLog(currentLog);

            const response = await sendChatMessage({
                message: trimmedMessage,
                history: currentLog,
                userName: userInfo?.name || 'Usuario',
                userEmail: userInfo?.email || '',
                sessionId,
                formData
            });

            setChatLog((prev) => [...prev, createChatEntry('gpt', response)]);
            setMessage('');
        } catch (err) {
            console.log("🚀 ~ Error al invocar el api del Chat")
        } finally {
            setLoading(false);
        }
    };

    const handleThumbUp = async (userMessage, gptResponse) => {
        await sendResponseFeedback({ userMessage, botResponse: gptResponse, ok: true });
    }

    const handleThumbDown = async (userMessage, gptResponse) => {
        await sendResponseFeedback({ userMessage, botResponse: gptResponse, ok: false });
    }

    const renderMessageContent = (text, isBot) => {
        if (!isBot) return <p className="whitespace-pre-line">{text}</p>;

        const normalized = text.replace(/(\d+)\.\s/g, '\n$1. ');
        const lines = normalized.split('\n').map((line) => line.trim()).filter(Boolean);
        const listItems = lines.filter((line) => /^\d+\./.test(line));
        const intro = lines.filter((line) => !/^\d+\./.test(line));

        if (listItems.length >= 2) {
            return (
                <>
                    {intro.length > 0 && intro.map((line, idx) => (
                        <p key={`intro-${idx}`} className="whitespace-pre-line">{line}</p>
                    ))}
                    <ol className="list-decimal list-inside space-y-1">
                        {listItems.map((item, idx) => (
                            <li key={`list-${idx}`}>{item.replace(/^\d+\.\s*/, '')}</li>
                        ))}
                    </ol>
                </>
            );
        }

        return <p className="whitespace-pre-line">{text}</p>;
    };

    // Conditional to show loading spinner if promises are not solved yet
    /**
    if (loading) {
        return <Loader />
    }
    **/

    return (
        <AdminLayout title="TherpyBot, Tu acompañante en cualquier momento">
            <div className="p-5 xl:p-7 pb-0 xl:pb-0 animate__animated animate__fadeIn">
                <div className="flex flex-col lg:flex-row gap-6">
                    <aside className="w-full lg:w-1/3">
                        <div className="rounded-2xl border border-green-100 bg-white shadow-sm p-5 space-y-4">
                            <div className="flex items-center gap-3">
                                <div className="h-12 w-12 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 text-white flex items-center justify-center text-lg font-semibold">
                                    {userInitials || '??'}
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Perfil</p>
                                    <p className="text-lg font-semibold text-green-900">{userInfo?.name || 'Sin nombre'}</p>
                                    <p className="text-sm text-gray-600">{userInfo?.email || 'Sin correo'}</p>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                                <div className="rounded-xl bg-green-50 border border-green-100 p-3">
                                    <p className="text-xs text-gray-500">Edad</p>
                                    <p className="text-base font-semibold text-green-900">{formData.user_age || '—'} años</p>
                                </div>
                                <div className="rounded-xl bg-green-50 border border-green-100 p-3">
                                    <p className="text-xs text-gray-500">Género</p>
                                    <p className="text-base font-semibold text-green-900">{formData.gender || '—'}</p>
                                </div>
                                <div className="rounded-xl bg-green-50 border border-green-100 p-3">
                                    <p className="text-xs text-gray-500">Peso</p>
                                    <p className="text-base font-semibold text-green-900">{formData.user_weight || '—'} kg</p>
                                </div>
                                <div className="rounded-xl bg-green-50 border border-green-100 p-3">
                                    <p className="text-xs text-gray-500">Altura</p>
                                    <p className="text-base font-semibold text-green-900">{formData.user_height || '—'} cm</p>
                                </div>
                            </div>
                            <div className="rounded-xl bg-gray-50 border border-gray-100 p-4 space-y-2 text-sm text-gray-700">
                                <p className="font-semibold text-green-900">Hábitos y salud</p>
                                <div className="flex flex-wrap gap-2">
                                    <span className="px-3 py-1 rounded-full bg-white border border-green-100 text-green-800">
                                        Ejercicio: {formData.does_exercise || '—'}
                                    </span>
                                    <span className="px-3 py-1 rounded-full bg-white border border-green-100 text-green-800">
                                        Alcohol: {formData.drink_alcohol || '—'}
                                    </span>
                                    <span className="px-3 py-1 rounded-full bg-white border border-green-100 text-green-800">
                                        Fuma: {formData.smoke || '—'}
                                    </span>
                                </div>
                                <div className="text-sm text-gray-600">
                                    <p>Medicamentos: {formData.medicines || '—'}</p>
                                    <p>Padecimientos: {formData.diseases || '—'}</p>
                                </div>
                            </div>
                        </div>
                    </aside>

                    <div className="flex-1 chatContainer bg-brown-100">
                        <div className="chatLog space-y-4">
                            {chatLog.map((chat, index) => {
                                const isBot = chat.sender === 'gpt';
                                return (
                                    <div key={index} className={`flex ${isBot ? 'justify-start' : 'justify-end'}`}>
                                        <div className={`flex w-full max-w-4xl gap-3 ${isBot ? '' : 'flex-row-reverse'}`}>
                                            <div className={`h-10 w-10 rounded-full flex items-center justify-center text-sm font-semibold shadow-sm ${isBot ? 'bg-gradient-to-br from-green-400 to-emerald-500 text-white' : 'bg-green-100 text-green-900'}`}>
                                                {isBot ? 'SB' : userInitials || 'Tú'}
                                            </div>
                                            <div className={`relative w-full rounded-2xl border shadow-sm ${isBot ? 'bg-white border-green-100' : 'bg-green-50 border-green-100'}`}>
                                                <div className="flex items-center justify-between px-4 pt-3 pb-2">
                                                    <div className="flex items-center gap-2">
                                                        <p className="text-sm font-semibold text-green-900">
                                                            {isBot ? 'SincronyBot' : userInfo?.name || 'Tú'}
                                                        </p>
                                                        <span className="text-xs text-gray-500">
                                                            {chat.time || ''}
                                                        </span>
                                                    </div>
                                                    <span className={`text-[11px] px-2 py-1 rounded-full ${isBot ? 'bg-green-100 text-green-700' : 'bg-green-200 text-green-900'}`}>
                                                        {isBot ? 'respuesta' : 'tú'}
                                                    </span>
                                            </div>
                                            <div className="px-4 pb-3">
                                                <div className="text-gray-800 leading-relaxed space-y-2">
                                                    {renderMessageContent(chat.text, isBot)}
                                                </div>
                                                {isBot && (
                                                    <div className="mt-4 flex items-center gap-3 pt-3 border-t border-gray-100 text-sm text-gray-600">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleThumbUp(chatLog[index - 1]?.text, chat.text)}
                                                                className="flex items-center gap-1 rounded-full px-3 py-1 bg-green-50 text-green-700 hover:bg-green-100 transition"
                                                            >
                                                                👍 <span className="text-xs font-medium">Útil</span>
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleThumbDown(chatLog[index - 1]?.text, chat.text)}
                                                                className="flex items-center gap-1 rounded-full px-3 py-1 bg-red-50 text-red-600 hover:bg-red-100 transition"
                                                            >
                                                                👎 <span className="text-xs font-medium">Mejorar</span>
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}

                            {loading ? <p>
                                <div className="border bg-green-200 shadow rounded-md p-4 max-w-sm w-full mx-auto">
                                    <div className="animate-pulse flex space-x-4">
                                        <div className="rounded-full bg-slate-200 h-10 w-10"></div>
                                        <div className="flex-1 space-y-6 py-1">
                                            <div className="h-2 bg-slate-200 rounded"></div>
                                            <div className="space-y-3">
                                                <div className="grid grid-cols-3 gap-4">
                                                    <div className="h-2 bg-slate-200 rounded col-span-2"></div>
                                                    <div className="h-2 bg-slate-200 rounded col-span-1"></div>
                                                </div>
                                                <div className="h-2 bg-slate-200 rounded"></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </p> : <p></p>}

                        </div>

                        <label htmlFor="chat" className="sr-only">Tú mensaje</label>
                        <div className="flex items-center px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-700">
                            <button type="button" className="inline-flex justify-center p-2 text-brown-400 rounded-lg cursor-pointer hover:text-green-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-600">
                                <svg className="w-5 h-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 18">
                                    <path fill="currentColor" d="M13 5.5a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0ZM7.565 7.423 4.5 14h11.518l-2.516-3.71L11 13 7.565 7.423Z" />
                                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 1H2a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1Z" />
                                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 5.5a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0ZM7.565 7.423 4.5 14h11.518l-2.516-3.71L11 13 7.565 7.423Z" />
                                </svg>
                                <span className="sr-only">Upload image</span>
                            </button>
                            <button type="button" className="p-2 text-brown-400 rounded-lg cursor-pointer hover:text-green-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-600">
                                <svg className="w-5 h-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
                                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.408 7.5h.01m-6.876 0h.01M19 10a9 9 0 1 1-18 0 9 9 0 0 1 18 0ZM4.6 11a5.5 5.5 0 0 0 10.81 0H4.6Z" />
                                </svg>
                                <span className="sr-only">Add emoji</span>
                            </button>
                            <textarea id="message" onChange={handleInputChange} value={message} rows="4" cols="500" className="block mx-4 p-2.5 w-full text-sm text-gray-900 bg-white rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500" placeholder="Tú mensaje..."></textarea>
                            <button onClick={handleSendMessage} type="submit" className="inline-flex justify-center p-2 text-blue-600 rounded-full cursor-pointer hover:bg-blue-100 dark:text-blue-500 dark:hover:bg-gray-600">
                                <svg className="w-5 h-5 rotate-90" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 18 20">
                                    <path d="m17.914 18.594-8-18a1 1 0 0 0-1.828 0l-8 18a1 1 0 0 0 1.157 1.376L8 18.281V9a1 1 0 0 1 2 0v9.281l6.758 1.689a1 1 0 0 0 1.156-1.376Z" />
                                </svg>
                                <span className="sr-only">Send message</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </AdminLayout>

    );
}

export default ChatComponent;
